import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Calendar, Clock, User } from 'lucide-react';
import { blogService } from '@/services/blogService';
import { BlogPost as BlogPostType } from '@/types/types';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      
      setIsLoading(true);
      try {
        const fetchedPost = await blogService.getPostBySlug(slug);
        
        if (!fetchedPost) {
          throw new Error('Post not found');
        }
        
        setPost(fetchedPost);
        setError(null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center gradient-bg">
        <div className="w-64 h-64">
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <PageLayout
        title="Post Not Found"
        description="We couldn't find the blog post you're looking for."
      >
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center">
          <h2 className="text-2xl font-bold mb-4">Page non disponible</h2>
          <p className="text-gray-500 mb-8">La page que vous recherchez n'est plus disponible.</p>
          <Button onClick={() => navigate('/blog')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Retourner vers le Blog
          </Button>
        </div>
      </PageLayout>
    );
  }

  if (!post) {
    return null;
  }

  // Calculate reading time (approx. 200 words per minute)
  const wordCount = post.content.split(/\s+/).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <PageLayout
      title={`${post.title} | Blog`}
      description={post.description}
    >
      <div className="max-w-4xl mx-auto">
        <Button 
          variant="ghost" 
          className="mb-6" 
          onClick={() => navigate('/blog')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />Blog
        </Button>

        <div className="space-y-8">
          {/* Hero image */}
          <div className="relative aspect-video rounded-lg overflow-hidden">
            <img 
              src={post.image} 
              alt={post.title} 
              className="w-full h-full object-cover"
            />
          </div>

          {/* Post metadata */}
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, index) => (
                <Badge key={index} variant="outline">{tag}</Badge>
              ))}
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">{post.title}</h1>
            
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-500">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{post.author.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{readingTime} min read</span>
              </div>
            </div>
          </div>

          {/* Post content */}
          <Card className="border-none shadow-sm">
            <CardContent className="p-6 md:p-8 prose prose-lg max-w-none">
              {/* This would be the actual content, potentially rendered as markdown */}
              <p className="lead">{post.description}</p>
              
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            </CardContent>
          </Card>

          {/* Author info */}
          <Card className="border-none shadow-sm bg-gray-50">
            <CardContent className="p-6 md:p-8">
              <div className="flex items-center gap-4">
                {post.author.avatar ? (
                  <img 
                    src={post.author.avatar} 
                    alt={post.author.name} 
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="h-8 w-8 text-gray-500" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-bold">{post.author.name}</h3>
                  <p className="text-gray-500">Author</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation buttons */}
          <div className="flex justify-between pt-8">
            <Button variant="outline" onClick={() => navigate('/blog')}>
              <ArrowLeft className="mr-2 h-4 w-4" /> All Posts
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default BlogPost;